import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {
  MatDialogClose,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { EjournalApiService } from '@et/api';
import { DirectivesModule } from '@et/directives';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import {
  EtJournalEntry,
  NotarialActType,
  AddEtJournalEntryDto,
  EtJournalEntryWithId,
  PatchEtJournalSignerDto,
  EtJournalSignerWithId,
  DEFAULT_DATE_PICKER,
} from '@et/typings';
import {
  tap,
  switchMap,
  finalize,
  catchError,
  EMPTY,
  map,
  forkJoin,
  of,
} from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';
import { convertToUTCDate, getCurrentUTCDate, transformDate } from '@et/utils';
import { NgTemplateOutlet } from '@angular/common';
import {
  DpDatePickerModule,
  IDatePickerDirectiveConfig,
} from 'ng2-date-picker';

export type JournalEntryAddEditComponentType = 'entry' | 'cancel_act';

export interface JournalEntryAddEditComponentData {
  entry?: EtJournalEntry;
  type?: JournalEntryAddEditComponentType;
}

@Component({
  selector: 'et-journal-entry-add-edit',
  imports: [
    ReactiveFormsModule,
    PresentationalAtomsModule,
    MatDialogClose,
    DirectivesModule,
    NgTemplateOutlet,
    DpDatePickerModule,
  ],
  templateUrl: './journal-entry-add-edit.component.html',
  styleUrl: './journal-entry-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalEntryAddEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef =
    inject<MatDialogRef<JournalEntryAddEditComponent>>(MatDialogRef);
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly ejournalService = inject(EjournalService);

  private readonly dialogData =
    inject<JournalEntryAddEditComponentData>(MAT_DIALOG_DATA);

  readonly datePickerConfig: IDatePickerDirectiveConfig = {
    ...DEFAULT_DATE_PICKER,
    format: 'MM/DD/YYYY',
  };

  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  readonly notorialActOptions = Object.values(NotarialActType);
  readonly signers = this.ejournalService.signers;

  readonly form = this.fb.nonNullable.group({
    notorialAct: [''],
    cancelActUtcDate: [''],
    cancelActUtcTime: [''],
    pageNumber: [null as number | null, Validators.min(1)],
    feeCharged: [null as number | null],
    documentDescription: [''],
    notes: [''],
    signerId: ['', [Validators.required]],
  });

  get isEditMode(): boolean {
    return !!this.dialogData.entry;
  }

  get isEntry(): boolean {
    return this.dialogData.type === 'entry';
  }

  get isCancelAct(): boolean {
    return this.dialogData.type === 'cancel_act';
  }

  ngOnInit(): void {
    const entry = this.dialogData?.entry;
    if (entry) {
      this.fillForm(entry);
    } else if (!this.isCancelAct) {
      this.form.patchValue({
        feeCharged: this.ejournalService.eJournal()?.feeCharged ?? null,
      });
    }
    this.setValidators();
  }

  /**
   * Validates the form, then adds a new entry or patches an existing one and links the selected signer.
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);
    this.dialogRef.disableClose = true;

    if (this.isEditMode && this.dialogData?.entry) {
      this.patchEntry(this.dialogData.entry);
    } else {
      this.addEntry();
    }
  }

  /**
   * Creates an entry via the API, appends it to local entries, and patches the selected signer to reference the new entry id.
   */
  private addEntry() {
    const raw = this.form.getRawValue();
    const ejournal = this.ejournalService.eJournal()!;
    const ipAddress = this.ejournalService.ip();

    const add: AddEtJournalEntryDto = {
      journalId: ejournal.id,
      packetId: ejournal.packetId,
      pageNumber: raw.pageNumber ? Number(raw.pageNumber) : undefined,
      notorialAct: raw.notorialAct,
      utcCreatedOn: getCurrentUTCDate(),
      documentDescription: raw.documentDescription,
      notes: raw.notes.trim() || undefined,
      feeCharged: Number(raw.feeCharged),
      cancelActUtcDate: raw.cancelActUtcDate
        ? convertToUTCDate(`${raw.cancelActUtcDate} ${raw.cancelActUtcTime}`)
        : undefined,
      ipAddress: ipAddress || '',
    };

    this.ejournalApiService
      .addEjournalEntry(add)
      .pipe(
        tap((created) => {
          this.ejournalService.updateEntries((entries) =>
            EjournalService.sortEntries([
              ...entries,
              this.withIdToEntry(created),
            ]),
          );
        }),
        switchMap((created) =>
          this.updateSigner(raw.signerId, this.withIdToEntry(created)),
        ),
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef.disableClose = false;
        }),
        catchError(() => {
          this.error.set('Failed to add entry');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  /**
   * Updates the entry and signer association in parallel, then refreshes local entry and signer state.
   *
   * @param entry - Existing entry being updated.
   */
  private patchEntry(entry: EtJournalEntry) {
    const raw = this.form.getRawValue();
    const entryId = entry.eJournalEntryId;
    const patch: Partial<EtJournalEntryWithId> = {
      id: entryId,
      eJournalPacketId: entry.eJournalPacketId,
      packetId: entry.packetId,
      uniqueId: entry.uniqueId,
      pageNumber: raw.pageNumber ? Number(raw.pageNumber) : undefined,
      notorialAct: raw.notorialAct,
      utcCreatedOn: entry.utcCreatedOn,
      feeCharged: this.isCancelAct ? undefined : Number(raw.feeCharged),
      cancelActUtcDate: raw.cancelActUtcDate
        ? convertToUTCDate(`${raw.cancelActUtcDate} ${raw.cancelActUtcTime}`)
        : undefined,
      documentDescription: raw.documentDescription,
      notes: raw.notes.trim(),
    };

    const updateSigner$ = this.updateSigner(raw.signerId, entry);

    const updateEntry$ = this.ejournalApiService
      .patchEjournalEntry(entryId, patch)
      .pipe(
        map((updated) => {
          this.ejournalService.updateEntries((entries) => {
            const mapped = this.withIdToEntry(updated);
            return entries.map((e) =>
              e.eJournalEntryId === mapped.eJournalEntryId ? mapped : e,
            );
          });
        }),
        catchError((error) => {
          this.error.set('Failed to update entry');
          throw error;
        }),
      );

    forkJoin([updateSigner$, updateEntry$])
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef.disableClose = false;
        }),
      )
      .subscribe(() => this.dialogRef.close());
  }

  /**
   * Syncs signer `ejournalEntryIds` with the API when the selected signer changes or when linking a new entry to a signer.
   * No-op when the signer is unchanged; may patch one or two signers when reassigning an entry.
   *
   * @param newSignerId - Signer selected in the form.
   * @param entry - Entry to attach or reattach (add flow uses the created entry; edit uses the loaded entry).
   * @returns Observable that completes when signer patches finish, or `of(EMPTY)` when no API call is needed.
   */
  private updateSigner(newSignerId: string, entry: EtJournalEntry) {
    const currentSigner = entry
      ? this.signers().find(
          (s) => s.ejournalEntryIds.indexOf(entry.eJournalEntryId) !== -1,
        )
      : null;
    const newSigner = this.signers().find(
      (s) => s.eJournalSignerId === newSignerId,
    );

    // Signer has NOT changed. Do nothing
    if (currentSigner?.eJournalSignerId === newSignerId) {
      return of(EMPTY);
    }

    // Signer has been added. Add entity to new signer
    if (newSigner && !currentSigner) {
      const newSignerPatch: Partial<PatchEtJournalSignerDto> = {
        id: newSigner.eJournalSignerId,
        ejournalEntryIds: JSON.parse(newSigner.ejournalEntryIds).concat(
          entry.eJournalEntryId,
        ),
      };
      const updateNewSigner$ = this.ejournalApiService
        .patchSigner(newSigner.eJournalSignerId, newSignerPatch)
        .pipe(map((s) => this.updateSignerInUI(s)));
      return updateNewSigner$;
    }

    // Signer has changed. Update both signers. Add entity to new signer and remove entity from current signer.
    if (
      currentSigner &&
      newSigner &&
      currentSigner.eJournalSignerId !== newSigner.eJournalSignerId
    ) {
      // Remove entity from current signer
      const currentSignerPatch: Partial<PatchEtJournalSignerDto> = {
        id: currentSigner.eJournalSignerId,
        ejournalEntryIds: JSON.parse(currentSigner.ejournalEntryIds).filter(
          (id: string) => id !== entry.eJournalEntryId,
        ),
      };
      const updateCurrentSigner$ = this.ejournalApiService
        .patchSigner(currentSigner.eJournalSignerId, currentSignerPatch)
        .pipe(map((s) => this.updateSignerInUI(s)));

      // Add entity to new signer
      const newSignerPatch: Partial<PatchEtJournalSignerDto> = {
        id: newSigner.eJournalSignerId,
        ejournalEntryIds: JSON.parse(newSigner.ejournalEntryIds).concat(
          entry.eJournalEntryId,
        ),
      };
      const updateNewSigner$ = this.ejournalApiService
        .patchSigner(newSigner.eJournalSignerId, newSignerPatch)
        .pipe(map((s) => this.updateSignerInUI(s)));

      return forkJoin([updateCurrentSigner$, updateNewSigner$]);
    }
    return of(EMPTY);
  }

  /**
   * Binds journal entry fields and resolves the signer whose `ejournalEntryIds` includes this entry.
   *
   * @param entry - Entry being edited.
   */
  private fillForm(entry: EtJournalEntry): void {
    this.form.patchValue({
      pageNumber: entry.pageNumber || null,
      notorialAct: entry.notorialAct ?? '',
      feeCharged: this.isCancelAct ? undefined : entry.feeCharged ?? null,
      documentDescription: entry.documentDescription ?? '',
      notes: entry.notes ?? '',
      cancelActUtcDate: entry.cancelActUtcDate
        ? transformDate(convertToUTCDate(entry.cancelActUtcDate), 'MM/DD/YYYY')
        : '',
      cancelActUtcTime: entry.cancelActUtcDate
        ? transformDate(entry.cancelActUtcDate, 'HH:mm')
        : '',
    });
    const linkedSigner = this.signers().find(
      (s) => s.ejournalEntryIds.indexOf(entry.eJournalEntryId) !== -1,
    );
    if (linkedSigner) {
      this.form.patchValue({
        signerId: linkedSigner.eJournalSignerId,
      });
    }
  }

  /**
   * Sets the validators for the form. Depends on the type of the entry (entry or cancel act).
   * @returns void
   */
  private setValidators() {
    if (this.isEntry) {
      this.form.controls.notorialAct.addValidators(Validators.required);
      this.form.controls.documentDescription.addValidators(Validators.required);
      this.form.controls.feeCharged.addValidators([
        Validators.required,
        Validators.min(0),
      ]);
    } else if (this.isCancelAct) {
      this.form.controls.cancelActUtcDate.addValidators(Validators.required);
      this.form.controls.notes.addValidators(Validators.required);
      this.form.controls.cancelActUtcTime.addValidators(Validators.required);
    }
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Merges an API signer response into the shared signers list, preserving existing signatures when present.
   *
   * @param updatedSigner - Signer returned from `patchSigner`.
   */
  private updateSignerInUI(updatedSigner: EtJournalSignerWithId) {
    const { id, ...signerWithoutId } = updatedSigner;
    const signer = {
      ...signerWithoutId,
      eJournalSignerId: id,
      signatures:
        this.signers().find((s) => s.eJournalSignerId === id)?.signatures ?? [],
    };
    this.ejournalService.updateSigners((signers) => {
      const signersCopy = [...signers];
      const index = signersCopy.findIndex(
        (s) => s.eJournalSignerId === signer.eJournalSignerId,
      );
      signersCopy[index] = signer;
      return signersCopy;
    });
  }

  /**
   * Maps API shape (`id`) to the UI `EtJournalEntry` shape (`eJournalEntryId`).
   *
   * @param w - Entry payload including `id` from the API.
   * @returns Entry with `eJournalEntryId` set from `id`.
   */
  private withIdToEntry(w: EtJournalEntryWithId): EtJournalEntry {
    const { id, ...rest } = w;
    return { ...rest, eJournalEntryId: id };
  }
}
